/**
 * Market tooltip mappings for the Overtime betting platform
 * Provides user-friendly descriptions for different market types
 */

/**
 * Gets the appropriate tooltip text for a given market name
 * @param marketName - The name of the market (case-insensitive)
 * @returns A descriptive tooltip explaining what the market type represents
 */
export const getMarketTooltip = (marketName: string): string => {
  const name = marketName.toLowerCase()

  // Core Winner Markets
  if (name.includes("match winner")) {
    return "Bet on the team that will win the match (1 = Home, 2 = Away, X = Draw)."
  }
  if (name.includes("winner") || name === "moneyline") {
    return "Who will win the match"
  }
  if (name === "1x2") {
    return "Standard three-way betting"
  }
  if (name.includes("12 including overtime")) {
    return "Bet on which team wins, including overtime (no draw option)"
  }
  if (name.includes("to qualify")) {
    return "Bet on which team will advance to the next stage"
  }
  if (name.includes("to win the cup")) {
    return "Bet on the team that will win the tournament"
  }

  // Spread/Handicap Markets
  if (name.includes("spread") && !name.includes("spread2")) {
    return "Point spread betting"
  }
  if (
    name.includes("handicap") &&
    !name.includes("asian") &&
    !name.includes("including overtime")
  ) {
    return "Will team 1 beat team 2 in the number of points adjusted for Handicap for the whole match"
  }
  if (
    name.includes("asian handicap") &&
    !name.includes("games") &&
    !name.includes("including overtime")
  ) {
    return "Asian handicap (no draw)"
  }
  if (name.includes("asian handicap games")) {
    return "Game-based handicap"
  }
  if (name.includes("asian handicap including overtime")) {
    return "Will team 1 beat team 2 in the number of points adjusted for Handicap for the whole match including the overtime"
  }
  if (name.includes("spread2")) {
    return "Secondary spread markets"
  }

  // Total Markets
  if (
    name === "total" ||
    (name.includes("total") &&
      !name.includes("totals") &&
      !name.includes("total2") &&
      !name.includes("total odd") &&
      !name.includes("under/over including overtime"))
  ) {
    return "Bet whether the total points/goals/runs will be over or under a set line"
  }
  if (name === "totals") {
    return "Bet whether the total points/goals/runs will be over or under a set line."
  }
  if (
    name.includes("over/under") &&
    !name.includes("asian") &&
    !name.includes("including overtime")
  ) {
    return "Standard totals"
  }
  if (name.includes("asian under/over")) {
    return "Asian total lines"
  }
  if (name.includes("under/over including overtime")) {
    return "Totals with overtime"
  }
  if (name.includes("total2")) {
    return "Secondary total markets"
  }
  if (name.includes("total odd/even") || name.includes("odd/even")) {
    return "Odd or even total score"
  }

  // Special Game Markets
  if (name.includes("double chance")) {
    return "A market that allows you to cover two of the three possible outcomes in a game with one bet. It is similar to full time result but effectively you back two outcomes in 1 bet."
  }
  if (name.includes("draw no bet")) {
    return "Bet refunded if draw"
  }
  if (name.includes("both teams to score")) {
    return "Both teams score yes/no"
  }

  // Time-Period Based Markets - Halves
  if (name.includes("1st half")) {
    return "Which team will win the 1st half of the match?"
  }
  if (name.includes("2nd half")) {
    return "Which team will win the 2nd half of the match?"
  }
  if (name.includes("first half")) {
    return "Which team will win the 1st half of the match?"
  }
  if (name.includes("second half")) {
    return "Which team will win the 2nd half of the match?"
  }
  if (name.includes("halves")) {
    return "General half markets"
  }

  // Quarters (Basketball/Football)
  if (name.includes("1st quarter")) {
    return "Will the total points by both teams be over or under the predicted score for specific Quarter of match?"
  }
  if (name.includes("2nd quarter")) {
    return "Will the total points by both teams be over or under the predicted score for specific Quarter of match?"
  }
  if (name.includes("3rd quarter")) {
    return "Will the total points by both teams be over or under the predicted score for specific Quarter of match?"
  }
  if (name.includes("4th quarter")) {
    return "Will the total points by both teams be over or under the predicted score for specific Quarter of match?"
  }
  if (name.includes("quarters")) {
    return "Will the total points by both teams be over or under the predicted score for specific Quarter of match?"
  }

  // Sets (Tennis/Volleyball)
  if (name.includes("1st set")) {
    return "Bet on the result of a specific set"
  }
  if (name.includes("2nd set")) {
    return "Bet on the result of a specific set"
  }
  if (name.includes("3rd set")) {
    return "Bet on the result of a specific set"
  }
  if (name.includes("sets")) {
    return "Bet on the result of a specific set"
  }

  // Innings (Baseball)
  if (name.includes("1st inning")) {
    return "Bet on the result within a specific number of innings."
  }
  if (name.includes("1st 3 innings")) {
    return "Bet on the result within a specific number of innings."
  }
  if (name.includes("1st 5 innings")) {
    return "Bet on the result within a specific number of innings."
  }
  if (name.includes("1st 7 innings")) {
    return "Bet on the result within a specific number of innings."
  }
  if (name.includes("innings")) {
    return "General inning markets"
  }

  // Games (Tennis)
  if (name.includes("games") && !name.includes("handicap")) {
    return "Tennis game markets"
  }

  // Player Props - Basketball
  if (name.includes("player points")) {
    return "Bet on total points scored by a player."
  }
  if (name.includes("player rebounds")) {
    return "Total rebounds by a player."
  }
  if (name.includes("player assists")) {
    return "betting on the total number of assists a player will achieve in a game"
  }
  if (name.includes("player threes")) {
    return "Bet on specific scoring stats."
  }
  if (name.includes("player two pointers")) {
    return "Bet on specific scoring stats."
  }
  if (name.includes("player free throws")) {
    return "Bet on specific scoring stats."
  }
  if (name.includes("player field goals")) {
    return "Bet on specific scoring stats."
  }
  if (name.includes("player blocks")) {
    return "A bet on the number of times a player successfully stops an opponent's shot attempt by blocking it before it reaches the basket."
  }
  if (name.includes("player steals")) {
    return "A bet on how many times a player legally takes the ball away from an opponent, resulting in change of possession."
  }
  if (name.includes("player turnovers")) {
    return "bet on the number of times a player loses possession of the ball to the opposing team, such as through bad passes, violations, or being stripped."
  }
  if (name.includes("player double double")) {
    return "Player to record double digits in two stats."
  }
  if (name.includes("player triple double")) {
    return "Player to record double digits in three stats"
  }
  if (name.includes("player combos")) {
    return "Combination props"
  }
  if (name.includes("player defense")) {
    return "Defensive stats"
  }

  // Football (Soccer) Player Props
  if (name.includes("goalscorer")) {
    return "Bet on a player to score a goal"
  }
  if (name.includes("shots on goal")) {
    return "Total shots on target by a player"
  }
  if (name.includes("player goals")) {
    return "Goals scored by a specific player"
  }
  if (name.includes("player shots")) {
    return "Total shots by player"
  }

  // American Football Player Props
  if (name.includes("td scorers")) {
    return "Player to score a touchdown"
  }
  if (name.includes("passing props")) {
    return "Passing statistics"
  }
  if (name.includes("rushing props")) {
    return "Rushing statistics"
  }
  if (name.includes("receiving props")) {
    return "Receiving statistics"
  }
  if (name.includes("d/st props")) {
    return "Defense/Special teams props"
  }
  if (name.includes("kicking props")) {
    return "Kicking statistics"
  }

  // Baseball Player Props
  if (name.includes("batter")) {
    return "Batter statistics"
  }
  if (name.includes("pitcher")) {
    return "Pitcher statistics"
  }

  // Tennis Player Props
  if (name.includes("aces")) {
    return "Aces served"
  }
  if (name.includes("break points won")) {
    return "Break points converted"
  }
  if (name.includes("double faults")) {
    return "Double faults committed"
  }

  // Hockey Player Props
  if (name.includes("goals") && name.includes("player")) {
    return "Goals scored"
  }
  if (name.includes("saves")) {
    return "Saves made"
  }
  if (name.includes("penalties")) {
    return "Penalty minutes"
  }

  // Cricket Player Props
  if (name.includes("runs")) {
    return "Runs scored"
  }
  if (name.includes("fours")) {
    return "Boundaries (4s)"
  }
  if (name.includes("sixes")) {
    return "Boundaries (6s)"
  }

  // Fighting/UFC Markets
  if (name.includes("winning method")) {
    return "How fight will end"
  }
  if (name.includes("fight props")) {
    return "Bet on fight-specific outcomes"
  }
  if (name.includes("round props")) {
    return "Bet on round-specific outcomes"
  }
  if (name.includes("go the distance")) {
    return "Will the fight last all scheduled rounds?"
  }
  if (name.includes("first minute finish")) {
    return "Will the fight end in the first minute?"
  }
  if (name.includes("will point be deducted")) {
    return "Will a point be deducted from a fighter?"
  }
  if (name.includes("winning round")) {
    return "Bet on which round the fight ends."
  }
  if (name.includes("ending method")) {
    return "Method of victory"
  }
  if (name.includes("method of victory")) {
    return "Specific method of how the winner is decided."
  }

  // Cricket Specific
  if (name.includes("total fours")) {
    return "Total boundary 4s"
  }
  if (name.includes("total sixes")) {
    return "Total boundary 6s"
  }
  if (name.includes("total runs outs")) {
    return "Runs and outs"
  }
  if (name.includes("most fours")) {
    return "Team with most 4s"
  }
  if (name.includes("most sixes")) {
    return "Team with most 6s"
  }
  if (name.includes("most runs outs")) {
    return "Team with most runs/outs"
  }

  // Baseball Specific
  if (name.includes("will there be overtime")) {
    return "Extra innings"
  }

  // Politics Markets
  if (name.includes("popular vote winner")) {
    return "Popular vote winner"
  }
  if (name.includes("winning party (states)")) {
    return "State-by-state party wins"
  }
  if (name.includes("winning party")) {
    return "Which party wins"
  }

  // SGP (Same Game Parlay) Markets
  if (name.includes("sgp") || name.includes("same game parlay")) {
    return "Combine multiple bets from the same game into one."
  }

  // Futures Markets
  if (name.includes("futures")) {
    return "Long-term outcome markets"
  }

  // Game Props
  if (name.includes("game props")) {
    return "General game properties"
  }
  if (name.includes("points") && !name.includes("player")) {
    return "Point-based props"
  }
  if (name.includes("corners")) {
    return "Corner kicks (soccer)"
  }

  // Combined Position Markets
  if (name.includes("halftime/fulltime")) {
    return "HT/FT results"
  }
  if (name.includes("winner & total")) {
    return "Combined winner and total bets"
  }

  // Exact Score Markets
  if (name.includes("correct score")) {
    return "Exact final score"
  }
  if (name.includes("total exact")) {
    return "Exact total points"
  }

  // Additional Missing Markets
  if (name.includes("championship winners")) {
    return "Championship winners"
  }
  if (name.includes("season-long props")) {
    return "Season-long props"
  }
  if (name.includes("tournament outcomes")) {
    return "Tournament outcomes"
  }

  // Position Names and Outcomes
  if (name.includes("yes/no")) {
    return "Binary outcomes"
  }
  if (
    name.includes("over/under") &&
    !name.includes("asian") &&
    !name.includes("including overtime") &&
    !name.includes("total")
  ) {
    return "Bet whether the total will be above or below a line."
  }
  if (name.includes("odd/even") && !name.includes("total")) {
    return "Total score will be odd or even"
  }
  if (name.includes("home/away/draw")) {
    return "Bet on home win, away win, or draw."
  }
  if (name.includes("1/2/x")) {
    return "Numeric/draw outcomes"
  }
  if (name.includes("+") || name.includes("-")) {
    return "Point spread values"
  }
  if (name.includes("team name")) {
    return "Team with spread"
  }

  // Generic player props fallback
  if (name.includes("player props") || name.includes("player")) {
    return "Bet on individual player performance statistics"
  }

  // Default tooltip for unknown market types
  return "Click to view betting options for this market"
}
